import React, { useState, useEffect } from "react";
import DeviceLayout from "./DeviceLayout";

const DeviceConfig = ({ deviceId }) => {
  const [liveConfigData, setLiveConfigData] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [jsonEditMode, setJsonEditMode] = useState(false);
  const [jsonRaw, setJsonRaw] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  const [viewMode, setViewMode] = useState("live");

  // Intentionally run once on mount: fetch live config and available history entries.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchConfig();
    fetchConfigHistory();
  }, []);

  const fetchWithTimeout = async (url, options, timeout = 15000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: "include"
      });
      clearTimeout(id);
      return response;
    } catch (err) {
      clearTimeout(id);
      throw err;
    }
  };

  const fetchConfig = async () => {
    const id = getDeviceIdFromUrl();
    if (id) {
      try {
        setLoading(true);
        setLiveError(null);

        const response = await fetchWithTimeout(`/api/config/${id}`, {}, 15000);
        if (!response.ok) {
          throw new Error(`Error fetching data: ${response.statusText}`);
        }

        const data = await response.json();
        setLiveConfigData(data);
        setConfigData(data);
        setFormData(data);
        if (viewMode === "live") {
          setJsonRaw(JSON.stringify(data, null, 2));
        }
      } catch (err) {
        setLiveError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      console.error("Device ID not found in the URL.");
    }
  };

  const fetchConfigHistory = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      return;
    }

    try {
      setHistoryLoading(true);
      setHistoryError(null);

      const response = await fetchWithTimeout(`/api/config/${id}/history`, {}, 15000);
      if (!response.ok) {
        throw new Error(`Error fetching history: ${response.statusText}`);
      }

      const data = await response.json();
      setHistoryItems(data);

      if (data.length > 0) {
        setSelectedHistoryId(String(data[0].id));
      } else {
        setSelectedHistoryId("");
      }
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getDeviceIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("id");
  };

  const handleInputChange = (key, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const loadHistoryConfig = async (configId) => {
    const deviceId = getDeviceIdFromUrl();
    if (!deviceId || !configId) {
      return;
    }

    try {
      setLoading(true);
      setLiveError(null);

      const response = await fetchWithTimeout(
        `/api/config/${deviceId}/history/${configId}`,
        {},
        15000
      );

      if (!response.ok) {
        throw new Error(`Error fetching saved config: ${response.statusText}`);
      }

      const data = await response.json();
      setConfigData(data);
      setFormData(data);
      setJsonRaw(JSON.stringify(data, null, 2));
      setEditMode(false);
      setJsonEditMode(false);
      setSelectedHistoryId(String(configId));
      setViewMode("history");
    } catch (err) {
      setLiveError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showLiveConfig = () => {
    setViewMode("live");
    setEditMode(false);
    setJsonEditMode(false);
    setLiveError(null);
    setConfigData(liveConfigData);
    if (liveConfigData) {
      setFormData(liveConfigData);
      setJsonRaw(JSON.stringify(liveConfigData, null, 2));
    }
  };

  const handleSubmit = async () => {
    const id = getDeviceIdFromUrl();
    if (id) {
      try {
        setLoading(true);
        setLiveError(null);

        const response = await fetch(`/api/config/${id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error(`Error updating data: ${response.statusText}`);
        }

        const updatedData = await response.json();
        setLiveConfigData(updatedData);
        setConfigData(updatedData);
        setFormData(updatedData);
        setEditMode(false);
        setJsonEditMode(false);
        setJsonRaw(JSON.stringify(updatedData, null, 2));
      } catch (err) {
        setLiveError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      console.error("Device ID not found in the URL.");
    }
  };

  const handleJsonSave = async () => {
    const id = getDeviceIdFromUrl();
    if (id) {
      try {
        const parsed = JSON.parse(jsonRaw);
        setLoading(true);
        setLiveError(null);

        const response = await fetch(`/api/config/${id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(parsed),
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error(`Error updating data: ${response.statusText}`);
        }

        const updatedData = await response.json();
        setLiveConfigData(updatedData);
        setConfigData(updatedData);
        setFormData(updatedData);
        setJsonRaw(JSON.stringify(updatedData, null, 2));
        setEditMode(false);
        setJsonEditMode(false);
      } catch (err) {
        setLiveError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      console.error("Device ID not found in the URL.");
    }
  };

  if (loading) {
    return <p>Loading configuration...</p>;
  }

  const displayData = viewMode === "history" ? configData : (liveConfigData || configData);

  const renderTable = (data) => {
    const rows = Object.entries(data);

    return (
      <div>
        Protocol: 0 = Sofar 00, 1 = Sofar 01, 2 = Pylon 00, 3 = Goodwe, 4 = Pylon 01, 5 = Solax<br />
        CellType: 0 = NMC lookup, 1 = LFP lookup, 2 = LFP CC, 3 = NMC CC
        <table>
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, value]) => (
              <tr key={key}>
                <td>{key}</td>
                <td>
                  {editMode ? (
                    typeof value === "object" ? (
                      <textarea
                        value={JSON.stringify(formData[key], null, 2)}
                        onChange={(e) =>
                          handleInputChange(key, JSON.parse(e.target.value))
                        }
                      />
                    ) : (
                      <input
                        type="text"
                        value={formData[key]}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                      />
                    )
                  ) : typeof value === "object" ? (
                    <pre>{JSON.stringify(value, null, 2)}</pre>
                  ) : (
                    value.toString()
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <DeviceLayout>
      <div>
        {liveError && (
          <p>
            Live config could not be loaded: {liveError}. You can still browse saved configs below.
          </p>
        )}

        <div style={{ marginBottom: "20px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button onClick={showLiveConfig} disabled={viewMode === "live"}>
            Live config
          </button>
          <button
            onClick={() => {
              if (selectedHistoryId) {
                loadHistoryConfig(selectedHistoryId);
              }
            }}
            disabled={historyLoading || !selectedHistoryId}
          >
            {historyLoading ? "Loading history..." : "Show saved config"}
          </button>
          <button onClick={fetchConfigHistory} disabled={historyLoading}>
            Refresh history
          </button>
        </div>

        {historyError && <p>Error loading history: {historyError}</p>}

        {historyItems.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="config-history-select">Saved configs</label>
            <div>
              <select
                id="config-history-select"
                value={selectedHistoryId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedHistoryId(nextId);
                  if (nextId) {
                    loadHistoryConfig(nextId);
                  }
                }}
              >
                {historyItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {new Date(item.received_at).toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {viewMode === "history" && (
          <p>
            Showing saved configuration {displayData.id ? `#${displayData.id}` : ""}
            {displayData.received_at ? ` from ${new Date(displayData.received_at).toLocaleString()}` : ""}
          </p>
        )}

        {!displayData ? (
          <p>No configuration data found. The device may be offline — you can still browse saved configs above.</p>
        ) : jsonEditMode ? (
          <div>
            <textarea
              style={{ width: "100%", height: "400px" }}
              value={jsonRaw}
              onChange={(e) => setJsonRaw(e.target.value)}
            />
            <div style={{ marginTop: "20px" }}>
              <button onClick={handleJsonSave}>Save JSON</button>
              <button
                onClick={() => {
                  setJsonEditMode(false);
                  setJsonRaw(JSON.stringify(displayData, null, 2));
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {renderTable(editMode ? formData : displayData)}
            <div style={{ marginTop: "20px" }}>
              {viewMode === "history" ? (
                <button onClick={showLiveConfig}>Back to live config</button>
              ) : editMode ? (
                <>
                  <button onClick={handleSubmit}>Save</button>
                  <button onClick={() => setEditMode(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditMode(true)}>Edit</button>
                  <button
                    onClick={() => {
                      setJsonRaw(JSON.stringify(formData, null, 2));
                      setJsonEditMode(true);
                    }}
                  >
                    Edit JSON
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </DeviceLayout>
  );
};

export default DeviceConfig;
